import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Modal } from 'semantic-ui-react';
import CSVFileSelection from './CSVFileSelection';
import SiteCSVWizard from './SiteCSVWizard';
import SiteFileData from './SiteFileData';
import { getImportModalTitleMessage } from './siteImportConfig';
import { useCSVFileImportModal } from './useCSVFileImportModal';
import additionalMessages from '../../messages';
import type {
  SiteGroupKind,
  SiteImportSubmitState,
} from './siteImportModalTypes';

type CSVFileImportModalPropsType = {
  handleClose: () => void;
  showModal: boolean;
  groupKind: SiteGroupKind;
};

function CSVFileImportModal({
  showModal,
  handleClose,
  groupKind,
}: CSVFileImportModalPropsType) {
  const { formatMessage } = useIntl();
  const {
    activeStep,
    fileSelection,
    handleNextStep,
    handlePrevStep,
    closeImportDialog,
    updateFileSelection,
  } = useCSVFileImportModal(showModal, handleClose);
  const [submitState, setSubmitState] = useState<SiteImportSubmitState | null>(
    null
  );

  const modalTitle = formatMessage(getImportModalTitleMessage(groupKind));
  const nextDisabled =
    fileSelection.fileName === '' ||
    fileSelection.fileLoading ||
    Boolean(fileSelection.error);

  useEffect(() => {
    if (activeStep === 1) {
      setSubmitState(null);
    }
  }, [activeStep]);

  const footerActions = useMemo(() => {
    if (activeStep === 1) {
      return (
        <>
          <Button onClick={closeImportDialog}>
            {formatMessage(additionalMessages.cancel)}
          </Button>
          <Button
            disabled={nextDisabled}
            loading={fileSelection.fileLoading}
            onClick={handleNextStep}
          >
            {formatMessage(additionalMessages.next)}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          onClick={closeImportDialog}
          disabled={submitState?.isSubmitting}
        >
          {formatMessage(additionalMessages.cancel)}
        </Button>
        <Button onClick={handlePrevStep} disabled={submitState?.isSubmitting}>
          {formatMessage(additionalMessages.previous)}
        </Button>
        <Button
          color="blue"
          disabled={!submitState?.canSubmit}
          loading={submitState?.isSubmitting}
          onClick={() => submitState?.onSubmit?.()}
        >
          {formatMessage(additionalMessages.createSites)}
        </Button>
      </>
    );
  }, [
    activeStep,
    closeImportDialog,
    fileSelection.fileLoading,
    formatMessage,
    handleNextStep,
    handlePrevStep,
    nextDisabled,
    submitState,
  ]);

  return (
    <Modal size="large" open={showModal} onClose={closeImportDialog}>
      <Modal.Header>
        {modalTitle}
        <Button
          circular
          icon="close"
          title="Close"
          floated="right"
          onClick={closeImportDialog}
        />
      </Modal.Header>
      <Modal.Content>
        <SiteCSVWizard step={activeStep} groupKind={groupKind} />
        {activeStep === 1 ? (
          <CSVFileSelection
            customClassName=""
            groupKind={groupKind}
            selectionState={fileSelection}
            onSelectionChange={updateFileSelection}
          />
        ) : (
          <SiteFileData
            data={fileSelection.fileData}
            fileType={fileSelection.fileType}
            groupKind={groupKind}
            closeFunc={closeImportDialog}
            onSubmitStateChange={setSubmitState}
          />
        )}
      </Modal.Content>
      <Modal.Actions>{footerActions}</Modal.Actions>
    </Modal>
  );
}

export default CSVFileImportModal;
